import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { CollectionSummary } from '@qkindex/contracts';
import { getCollectionDataStore } from '../collection/collectionData';

/** Share existing capacity subscriptions; no financial data exposed to collaborators. */
export const useCollectionBillingAccess = (
  userId: string,
  collections: CollectionSummary[],
) => {
  const store = getCollectionDataStore(userId);
  const contextKey = JSON.stringify(
    collections
      .filter((entry) => entry.role !== 'viewer')
      .map(({ collectionId, membershipVersion, role }) => ({
        collectionId,
        membershipVersion,
        role,
      })),
  );
  const contexts = useMemo(
    () =>
      JSON.parse(contextKey) as Array<
        Pick<CollectionSummary, 'collectionId' | 'membershipVersion' | 'role'>
      >,
    [contextKey],
  );
  const subscribe = useCallback(
    (listener: () => void) => {
      const unsubscribe = contexts.map((context) =>
        store.subscribeCapacity(context, listener),
      );
      return () => unsubscribe.forEach((stop) => stop());
    },
    [store, contexts],
  );
  const getSnapshot = useCallback(
    () =>
      JSON.stringify(
        contexts
          .filter((context) => {
            const snapshot = store.getCapacitySnapshot(context);
            const state = snapshot.displayState ?? snapshot.state;
            return (
              state.status === 'ready' &&
              ['restricted', 'deleting'].includes(state.value.access)
            );
          })
          .map(({ collectionId }) => collectionId),
      ),
    [store, contexts],
  );
  const blocked = useSyncExternalStore(subscribe, getSnapshot);
  return useMemo(() => new Set<string>(JSON.parse(blocked)), [blocked]);
};
